import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { CHAT_PARTS_MAX } from '@arthelper/shared/chat-parts';
import { Button } from '@artstorefronts/ui';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../../components/AdminLayout';
import ChatDisplay from '../../../components/ChatDisplay';
import RecentPartNotice from '../../../components/Chat/RecentPartNotice';
import ChatEvaluation from '../../../components/ChatEvaluation';
import AdminChatDetails from '../../../components/AdminChatDetails';

const ADMIN_CHAT_QUERY = gql`
  query AdminChat($id: ID!, $last: Int = ${CHAT_PARTS_MAX}) {
    chat(id: $id) {
      id
      title
      type
      targetId
      targetType
      createdAt
      updatedAt
      good
      notes
      user {
        asfId
        firstName
        lastName
        email
        stripeCustomerId
        isOldAsfMember
        numImages
        numChats
        numSocialPosts
        hasSettingsInstructions
        settingsInstructions
      }
      partCount
      parts(last: $last) {
        id
        role
        partIndex
        type
        content
        toolCall
        attachment
        reasoning
        usage
        metadata
        good
        notes
        createdAt
      }
      summary {
        summaryContent
      }
    }
  }
`;

function AdminChatDetail() {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery(ADMIN_CHAT_QUERY, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return (
      <AdminLayout title={t('chats.detail.loading-title')} description={t('chats.detail.loading-description')}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !data?.chat) {
    return (
      <AdminLayout title={t('chats.detail.not-found-title')} description={t('chats.detail.not-found-description')}>
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error ? error.message : t('chats.detail.not-found-body')}</p>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Button asChild variant="outline">
            <Link to="/admin/chats" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t('chats.detail.back-to-chats')}
            </Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const chat = data.chat;

  return (
    <AdminLayout
      title={chat.title}
      description={t('chats.detail.conversation-with', {
        user: chat.user?.email || t('chats.detail.unknown-user'),
      })}
      action={
        <Button asChild variant="outline">
          <Link to="/admin/chats" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            {t('chats.detail.back-to-chats')}
          </Link>
        </Button>
      }
    >
      {/* Break out of the container to go full width */}
      <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
        {/* Create a flex container that takes available height */}
        <div className="flex flex-col h-[70vh] -mt-10">
          {/* Full-width 60/40 split layout */}
          <div className="flex min-h-0 flex-1">
            {/* Left Panel (60%) - Chat Messages Only - Scrollable */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* Chat Messages */}
              <div className="bg-white border-l border-r border-b">
                <div className="p-4 border-b">
                  <h3 className="text-lg font-medium">{t('chats.detail.messages-heading')}</h3>
                </div>
                <div className="p-4">
                  <RecentPartNotice chat={chat} />
                  <ChatDisplay chatParts={chat.parts} />
                </div>
              </div>
            </div>

            {/* Right Panel (40%) - Evaluation Form and Chat Details */}
            <div className="w-2/5 overflow-y-auto p-6 space-y-6">
              {/* Chat Type */}
              <div className="text-left">
                <span className="text-xl font-semibold text-black tracking-tight">
                  {chat.type === 'standalone'
                    ? t('chats.detail.open-chat')
                    : chat.type.charAt(0).toUpperCase() + chat.type.slice(1)}
                </span>
              </div>

              <ChatEvaluation chatId={chat.id} initialGood={chat.good} initialNotes={chat.notes} />

              {/* Chat Details */}
              <AdminChatDetails chat={chat} />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminChatDetail;
